import React from 'react';
import Select, {
    ActionMeta,
    ControlProps,
    GroupBase,
    MenuProps,
    MultiValueProps,
    OptionProps,
    PlaceholderProps,
    SingleValueProps,
    ValueContainerProps
} from 'react-select';
import Creatable from 'react-select/creatable';
import { Chip, emphasize, MenuItem, Paper, TextField, Theme, Typography, styled } from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import clsx from 'clsx';
import { v4 as uuid } from 'uuid';
import { Option } from 'lib/types';
import { Column } from 'ui/Flex';

const PREFIX = 'MultiSelectSuggest';

const classes = {
    root: `${PREFIX}-root`,
    input: `${PREFIX}-input`,
    valueContainer: `${PREFIX}-valueContainer`,
    chip: `${PREFIX}-chip`,
    chipFocused: `${PREFIX}-chipFocused`,
    noOptionsMessage: `${PREFIX}-noOptionsMessage`,
    singleValue: `${PREFIX}-singleValue`,
    placeholder: `${PREFIX}-placeholder`,
    paper: `${PREFIX}-paper`,
    divider: `${PREFIX}-divider`,
    clearIndicator: `${PREFIX}-clearIndicator`
};

const applyStyles = ({ theme }: { theme: Theme }) => ({
    [`& .${classes.root}`]: {
        flexGrow: 1,
        height: 250,
        minWidth: 290
    },

    [`& .${classes.input}`]: {
        display: 'flex',
        paddingBottom: theme.spacing(0.25),
        paddingTop: theme.spacing(0.5),
        paddingLeft: 0,
        paddingRight: 0,
        height: 'auto'
    },

    [`& .${classes.valueContainer}`]: {
        display: 'flex',
        flexWrap: 'wrap',
        flex: 1,
        alignItems: 'center',
        overflow: 'hidden'
    },

    [`& .${classes.chip}`]: {
        margin: theme.spacing(0.5, 0.25)
    },

    [`& .${classes.chipFocused}`]: {
        backgroundColor: emphasize(
            theme.palette.mode === 'light' ? theme.palette.grey[300] : theme.palette.grey[700],
            0.08
        )
    },

    [`& .${classes.noOptionsMessage}`]: {
        padding: theme.spacing(1, 2)
    },

    [`& .${classes.singleValue}`]: {
        fontSize: 16
    },

    [`& .${classes.placeholder}`]: {
        position: 'absolute',
        left: 2,
        bottom: 6,
        fontSize: 16,
        color: theme.palette.text.disabled
    },

    [`& .${classes.paper}`]: {
        position: 'absolute',
        zIndex: 1000000,
        marginTop: theme.spacing(1),
        left: 0,
        right: 0
    },

    [`& .${classes.divider}`]: {
        height: theme.spacing(2)
    },

    [`& .${classes.clearIndicator}`]: {
        padding: theme.spacing(0.25),
        cursor: 'pointer'
    }
});

const StyledCreatable = styled(Creatable)(applyStyles) as any;
const StyledSelect = styled(Select)(applyStyles) as any;

type SelectValue = readonly Option[] | Option | null;

export interface MultiSelectSuggestProps {
    label?: React.ReactNode;
    disabled?: boolean;
    name?: string;
    onBlur?: (e: React.FocusEvent) => void;
    options?: Option[];
    placeholder?: string | React.ReactNode;
    value: SelectValue;
    onChange: (value: SelectValue, actionMeta?: ActionMeta<Option>) => void;
    inputValue?: string | undefined;
    menuIsOpen?: boolean;
    onInputChange?: (newValue: string) => void;
    onKeyDown?: React.KeyboardEventHandler;
    isCreatable?: boolean;
    isMulti?: boolean;
    isClearable?: boolean;
    error?: boolean;
    helperText?: React.ReactNode;
    className?: string;
    showNoOptionsMessage?: boolean;
}

function NoOptionsMessage(props: MenuProps<Option, boolean, GroupBase<Option>>) {
    return (
        <Typography
            color="textSecondary"
            className={(props.selectProps as any).classes.noOptionsMessage}
            {...props.innerProps}
        >
            {props.children}
        </Typography>
    );
}

function inputComponent({ inputRef, ...rest }: any) {
    return <div ref={inputRef} {...rest} />;
}

function Control(props: ControlProps<Option, boolean, GroupBase<Option>>) {
    const {
        children,
        innerProps,
        innerRef,
        selectProps: { isDisabled }
    } = props;
    const TextFieldProps = (props.selectProps as any).TextFieldProps;

    return (
        <TextField
            fullWidth
            disabled={isDisabled}
            InputProps={{
                inputComponent,
                inputProps: {
                    children,
                    className: classes.input,
                    ref: innerRef,
                    ...innerProps
                }
            }}
            {...TextFieldProps}
        />
    );
}

function OptionComponent(props: OptionProps<Option, boolean, GroupBase<Option>>) {
    return (
        <MenuItem
            ref={props.innerRef}
            selected={props.isFocused}
            component="div"
            style={{
                fontWeight: props.isSelected ? 500 : 400
            }}
            {...props.innerProps}
        >
            {props.children}
        </MenuItem>
    );
}

function Placeholder(props: PlaceholderProps<Option, boolean, GroupBase<Option>>) {
    const { innerProps = {}, children } = props;
    return (
        <Typography
            color="textSecondary"
            className={(props.selectProps as any).classes.placeholder}
            {...innerProps}
        >
            {children}
        </Typography>
    );
}

function SingleValue(props: SingleValueProps<Option, boolean, GroupBase<Option>>) {
    return (
        <Typography
            component="div"
            className={(props.selectProps as any).classes.singleValue}
            {...props.innerProps}
        >
            {props.children}
        </Typography>
    );
}

function ValueContainer(props: ValueContainerProps<Option, boolean, GroupBase<Option>>) {
    return <div className={(props.selectProps as any).classes.valueContainer}>{props.children}</div>;
}

function MultiValue(props: MultiValueProps<Option, boolean, GroupBase<Option>>) {
    return (
        <Chip
            key={typeof props.children === 'string' ? props.children : uuid()}
            tabIndex={-1}
            label={props.children}
            className={clsx((props.selectProps as any).classes.chip, {
                [(props.selectProps as any).classes.chipFocused]: props.isFocused
            })}
            onDelete={props.removeProps.onClick}
            deleteIcon={<CancelIcon {...(props.removeProps as any)} />}
        />
    );
}

function Menu(props: MenuProps<Option, boolean, GroupBase<Option>>) {
    return (
        <Paper square className={(props.selectProps as any).classes.paper} {...props.innerProps}>
            {props.children}
        </Paper>
    );
}

function Empty(): React.ReactElement {
    return null;
}

const formatOptionLabel = ({ description, label }: Option) =>
    description ? (
        <Column>
            {label}
            <Typography variant="caption">{description}</Typography>
        </Column>
    ) : (
        label
    );

export function MultiSelectSuggest(props: MultiSelectSuggestProps) {
    const {
        value,
        onChange,
        options,
        disabled,
        label,
        placeholder,
        inputValue,
        onBlur,
        menuIsOpen,
        onInputChange,
        onKeyDown,
        isCreatable,
        error,
        isMulti = true,
        helperText,
        name,
        isClearable,
        showNoOptionsMessage = true,
        className
    } = props;

    const Component: any = isCreatable ? StyledCreatable : StyledSelect;
    const uniqIdentifier = React.useMemo(() => (label ? label : uuid()), [label]);

    const renderNoOptionsMessage = React.useCallback(
        () => (showNoOptionsMessage ? 'No options' : null),
        [showNoOptionsMessage]
    );

    return (
        <Component
            classes={classes}
            inputId={`react-select-multiple-${uniqIdentifier}`}
            TextFieldProps={{
                label,
                error,
                helperText,
                InputLabelProps: {
                    htmlFor: `react-select-multiple-${uniqIdentifier}`,
                    shrink: true
                }
            }}
            styles={{ clearIndicator: (base: any) => ({ ...base, padding: 2 }) }}
            onBlur={onBlur}
            name={name}
            isDisabled={disabled}
            placeholder={placeholder}
            options={options}
            components={{
                Control,
                Menu,
                MultiValue,
                Placeholder,
                SingleValue,
                ValueContainer,
                Option: OptionComponent,
                DropdownIndicator: Empty,
                IndicatorSeparator: Empty,
                NoOptionsMessage
            }}
            formatOptionLabel={formatOptionLabel}
            value={value ?? ''}
            onChange={onChange}
            isMulti={isMulti}
            inputValue={inputValue}
            isClearable={isClearable ?? true}
            menuIsOpen={menuIsOpen}
            onInputChange={onInputChange}
            onKeyDown={onKeyDown}
            className={className}
            noOptionsMessage={renderNoOptionsMessage}
        />
    );
}
